import { defineMessages } from '@/i18n'

/**
 * The Aufmaß screen: filter rail / sheet, the editable grid's chrome and the search dropdown.
 * The grid's column labels are the AU workbook's headers — data, so they are not in here.
 */
export const aufmassMessages = defineMessages({
  en: {
    // stats
    crewDaysLabel: 'Crew-days',
    dataAsOf: 'Data as of',
    refreshed: 'refreshed {ago}',
    justNow: 'just now',
    minAgo: '{count} min ago',
    hoursAgo: '{count} h ago',
    daysAgo: '{count} d ago',
    // export
    exportExcel: 'Export Excel',
    exportExcelRows: { one: 'Export Excel · {count} row', other: 'Export Excel · {count} rows' },
    preparingExcel: 'Preparing Excel…',
    preparingExcelLabel: 'Preparing Excel',
    exportTooBig: 'Filter to {max} rows or fewer to export · now {now}. A date range, project or firm does it.',
    // filters
    searchPlaceholder: 'street, city, crew, firma…',
    project: 'Project',
    allProjects: 'All projects',
    findProject: 'Type to find a project…',
    firma: 'Firma',
    allFirms: 'All firms',
    findFirm: 'Type to find a firm…',
    record: 'Record',
    allRecords: 'All records',
    reportFiled: 'Report filed',
    clockedInOnly: 'Clocked in only',
    inferred: 'Inferred',
    cabling: 'Cabling',
    allWork: 'All work',
    anyCabling: 'Any cabling',
    sortLabel: 'Sort (or tap a column header)',
    sort: 'Sort',
    newestFirst: 'Newest first',
    oldestFirst: 'Oldest first',
    mostMetres: 'Most metres',
    highestSub: 'Highest SUB €',
    crewAz: 'Crew A–Z',
    mjestoAz: 'Mjesto A–Z',
    withWorkOnly: 'With work only',
    positionColumns: 'Position columns',
    headers: 'Headers',
    headersFull: 'Full',
    headersShort: 'Short',
    headersFullTitle: 'Full stacked labels',
    headersShortTitle: 'Short one-line names',
    hideEmptyColumns: 'Hide empty columns',
    zoom: 'Zoom',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    zoomReset: 'Reset to 100%',
    showFilters: 'Show filters',
    hideFilters: 'Hide filters',
    sheetSubtitle: { one: '{count} crew-day · data as of {date}', other: '{count} crew-days · data as of {date}' },
    showCrewDays: { one: 'Show {count} crew-day', other: 'Show {count} crew-days' },
    couldNotLoadMeasurements: 'Could not load measurements.',
    // status line
    tapToEdit: 'Tap a cell, then edit in the bar',
    desktopEditHint: 'Type to edit · Enter/Tab commits · Esc cancels · ⌘/Ctrl+⌫ reverts to AU',
    readOnlyNeedsPermission: 'Read-only — editing needs CanEditAufmass',
    correctedCells: { one: '{count} corrected cell on this page', other: '{count} corrected cells on this page' },
    you: 'you',
    // grid
    gridAria: 'Aufmaß spreadsheet',
    formulaBarAria: 'Formula bar — selected cell value',
    editSelectedCell: 'Edit the selected cell…',
    readOnlyDerived: 'Read-only — derived from AU',
    clickToSort: 'Click to sort',
    editable: 'Editable',
    couldNotSave: 'Could not save that cell.',
    correctedBy: 'Corrected by {name} on {date}. AU says: {original}',
    empty: '(empty)',
    rtkChanged: 'RTK measurement changed: Total Metri moved {delta} m after a map correction.',
    romNotSet: 'data not set by user',
    presenceReport: 'report',
    presenceClocked: 'clocked',
    presenceInferred: 'inferred',
    presenceReportTitle: 'A report was filed for this crew-day',
    presenceClockedTitle: 'Crew clocked in but filed no report',
    presenceInferredTitle: 'No AU record for this crew-day — inferred because the crew was active either side of it',
    flagMarked: 'Marked for resolution',
    flagQuantityDetail:
      'Quantity far beyond anything the source plausibly holds. The largest genuine crew-day '
      + 'on record is 663 m, so anything past 2,000 m is marked for review.',
    flagNegativeHoursDetail:
      'Negative man-hours: the shift has an end time, but it is before the start (or the break '
      + 'is longer than the span), so the length comes out below zero. Open shifts with no '
      + 'clock-out are not flagged — those hours are left blank, not subtracted.',
    // search select
    noMatches: 'No matches',
    unnamed: '(unnamed)',
    showingFirst: 'Showing first {max} — keep typing to narrow.',
  },
  de: {
    crewDaysLabel: 'Kolonnentage',
    dataAsOf: 'Datenstand',
    refreshed: 'aktualisiert {ago}',
    justNow: 'gerade eben',
    minAgo: 'vor {count} Min.',
    hoursAgo: 'vor {count} Std.',
    daysAgo: { one: 'vor {count} Tag', other: 'vor {count} Tagen' },
    exportExcel: 'Excel exportieren',
    exportExcelRows: { one: 'Excel exportieren · {count} Zeile', other: 'Excel exportieren · {count} Zeilen' },
    preparingExcel: 'Excel wird erstellt…',
    preparingExcelLabel: 'Excel wird erstellt',
    exportTooBig: 'Für den Export auf höchstens {max} Zeilen filtern · aktuell {now}. Ein Zeitraum, ein Projekt oder eine Firma genügt.',
    searchPlaceholder: 'Straße, Ort, Kolonne, Firma…',
    project: 'Projekt',
    allProjects: 'Alle Projekte',
    findProject: 'Projekt suchen…',
    firma: 'Firma',
    allFirms: 'Alle Firmen',
    findFirm: 'Firma suchen…',
    record: 'Erfassung',
    allRecords: 'Alle Erfassungen',
    reportFiled: 'Bericht eingereicht',
    clockedInOnly: 'Nur eingestempelt',
    inferred: 'Abgeleitet',
    cabling: 'Verkabelung',
    allWork: 'Alle Arbeiten',
    anyCabling: 'Mit Verkabelung',
    sortLabel: 'Sortierung (oder Spaltenkopf antippen)',
    sort: 'Sortierung',
    newestFirst: 'Neueste zuerst',
    oldestFirst: 'Älteste zuerst',
    mostMetres: 'Meiste Meter',
    highestSub: 'Höchster SUB €',
    crewAz: 'Kolonne A–Z',
    mjestoAz: 'Mjesto A–Z',
    withWorkOnly: 'Nur mit Arbeit',
    positionColumns: 'Positionsspalten',
    headers: 'Kopfzeilen',
    headersFull: 'Voll',
    headersShort: 'Kurz',
    headersFullTitle: 'Vollständige, gestapelte Bezeichnungen',
    headersShortTitle: 'Kurze, einzeilige Namen',
    hideEmptyColumns: 'Leere Spalten ausblenden',
    zoom: 'Zoom',
    zoomOut: 'Verkleinern',
    zoomIn: 'Vergrößern',
    zoomReset: 'Auf 100 % zurücksetzen',
    showFilters: 'Filter anzeigen',
    hideFilters: 'Filter ausblenden',
    sheetSubtitle: { one: '{count} Kolonnentag · Datenstand {date}', other: '{count} Kolonnentage · Datenstand {date}' },
    showCrewDays: { one: '{count} Kolonnentag anzeigen', other: '{count} Kolonnentage anzeigen' },
    couldNotLoadMeasurements: 'Messungen konnten nicht geladen werden.',
    tapToEdit: 'Zelle antippen, dann in der Leiste bearbeiten',
    desktopEditHint: 'Tippen zum Bearbeiten · Enter/Tab übernimmt · Esc bricht ab · ⌘/Strg+⌫ stellt den AU-Wert wieder her',
    readOnlyNeedsPermission: 'Nur Lesen — Bearbeiten erfordert CanEditAufmass',
    correctedCells: { one: '{count} korrigierte Zelle auf dieser Seite', other: '{count} korrigierte Zellen auf dieser Seite' },
    you: 'Ihnen',
    gridAria: 'Aufmaß-Tabelle',
    formulaBarAria: 'Bearbeitungsleiste — Wert der ausgewählten Zelle',
    editSelectedCell: 'Ausgewählte Zelle bearbeiten…',
    readOnlyDerived: 'Nur Lesen — aus AU abgeleitet',
    clickToSort: 'Zum Sortieren klicken',
    editable: 'Bearbeitbar',
    couldNotSave: 'Diese Zelle konnte nicht gespeichert werden.',
    correctedBy: 'Korrigiert von {name} am {date}. In AU: {original}',
    empty: '(leer)',
    rtkChanged: 'RTK-Messung geändert: Total Metri hat sich nach einer Kartenkorrektur um {delta} m verändert.',
    romNotSet: 'vom Benutzer nicht angegeben',
    presenceReport: 'Bericht',
    presenceClocked: 'eingestempelt',
    presenceInferred: 'abgeleitet',
    presenceReportTitle: 'Für diesen Kolonnentag wurde ein Bericht eingereicht',
    presenceClockedTitle: 'Kolonne hat eingestempelt, aber keinen Bericht eingereicht',
    presenceInferredTitle: 'Kein AU-Eintrag für diesen Kolonnentag — abgeleitet, weil die Kolonne davor und danach aktiv war',
    flagMarked: 'Zur Klärung markiert',
    flagQuantityDetail:
      'Menge weit über allem, was die Quelle plausibel enthalten kann. Der größte echte Kolonnentag '
      + 'liegt bei 663 m, daher wird alles über 2.000 m zur Prüfung markiert.',
    flagNegativeHoursDetail:
      'Negative Arbeitsstunden: Die Schicht hat ein Ende, das aber vor dem Beginn liegt (oder die Pause '
      + 'ist länger als die Schicht), sodass die Dauer unter null fällt. Offene Schichten ohne '
      + 'Ausstempeln werden nicht markiert — diese Stunden bleiben leer, sie werden nicht abgezogen.',
    noMatches: 'Keine Treffer',
    unnamed: '(ohne Namen)',
    showingFirst: 'Die ersten {max} werden angezeigt — zum Eingrenzen weitertippen.',
  },
  cnr: {
    crewDaysLabel: 'Dani kolona',
    dataAsOf: 'Podaci do',
    refreshed: 'osvježeno {ago}',
    justNow: 'upravo sada',
    minAgo: 'prije {count} min',
    hoursAgo: 'prije {count} h',
    daysAgo: { one: 'prije {count} dan', few: 'prije {count} dana', other: 'prije {count} dana' },
    exportExcel: 'Izvezi Excel',
    exportExcelRows: { one: 'Izvezi Excel · {count} red', few: 'Izvezi Excel · {count} reda', other: 'Izvezi Excel · {count} redova' },
    preparingExcel: 'Pripremam Excel…',
    preparingExcelLabel: 'Pripremam Excel',
    exportTooBig: 'Za izvoz suzite filter na najviše {max} redova · trenutno {now}. Dovoljan je period, projekat ili firma.',
    searchPlaceholder: 'ulica, mjesto, kolona, firma…',
    project: 'Projekat',
    allProjects: 'Svi projekti',
    findProject: 'Upišite naziv projekta…',
    firma: 'Firma',
    allFirms: 'Sve firme',
    findFirm: 'Upišite naziv firme…',
    record: 'Zapis',
    allRecords: 'Svi zapisi',
    reportFiled: 'Izvještaj predat',
    clockedInOnly: 'Samo prijavljen dolazak',
    inferred: 'Izvedeno',
    cabling: 'Kabliranje',
    allWork: 'Svi radovi',
    anyCabling: 'Bilo koje kabliranje',
    sortLabel: 'Sortiranje (ili dodirnite zaglavlje kolone)',
    sort: 'Sortiranje',
    newestFirst: 'Najnovije prvo',
    oldestFirst: 'Najstarije prvo',
    mostMetres: 'Najviše metara',
    highestSub: 'Najveći SUB €',
    crewAz: 'Kolona A–Z',
    mjestoAz: 'Mjesto A–Z',
    withWorkOnly: 'Samo sa radom',
    positionColumns: 'Kolone pozicija',
    headers: 'Zaglavlja',
    headersFull: 'Puna',
    headersShort: 'Kratka',
    headersFullTitle: 'Puni nazivi u više redova',
    headersShortTitle: 'Kratki nazivi u jednom redu',
    hideEmptyColumns: 'Sakrij prazne kolone',
    zoom: 'Uvećanje',
    zoomOut: 'Umanji',
    zoomIn: 'Uvećaj',
    zoomReset: 'Vrati na 100%',
    showFilters: 'Prikaži filtere',
    hideFilters: 'Sakrij filtere',
    sheetSubtitle: {
      one: '{count} dan kolone · podaci do {date}',
      few: '{count} dana kolona · podaci do {date}',
      other: '{count} dana kolona · podaci do {date}',
    },
    showCrewDays: { one: 'Prikaži {count} dan kolone', few: 'Prikaži {count} dana kolona', other: 'Prikaži {count} dana kolona' },
    couldNotLoadMeasurements: 'Nije moguće učitati mjerenja.',
    tapToEdit: 'Dodirnite ćeliju, pa je izmijenite u traci',
    desktopEditHint: 'Kucajte za izmjenu · Enter/Tab potvrđuje · Esc otkazuje · ⌘/Ctrl+⌫ vraća vrijednost iz AU',
    readOnlyNeedsPermission: 'Samo za čitanje — za izmjene je potrebno CanEditAufmass',
    correctedCells: {
      one: '{count} ispravljena ćelija na ovoj strani',
      few: '{count} ispravljene ćelije na ovoj strani',
      other: '{count} ispravljenih ćelija na ovoj strani',
    },
    you: 'vi',
    gridAria: 'Aufmaß tabela',
    formulaBarAria: 'Traka formule — vrijednost odabrane ćelije',
    editSelectedCell: 'Izmijenite odabranu ćeliju…',
    readOnlyDerived: 'Samo za čitanje — izvedeno iz AU',
    clickToSort: 'Kliknite za sortiranje',
    editable: 'Može se mijenjati',
    couldNotSave: 'Nije moguće sačuvati ćeliju.',
    correctedBy: 'Ispravka: {name}, {date}. U AU: {original}',
    empty: '(prazno)',
    rtkChanged: 'RTK mjerenje izmijenjeno: Total Metri se promijenio za {delta} m nakon ispravke na mapi.',
    romNotSet: 'korisnik nije unio podatak',
    presenceReport: 'izvještaj',
    presenceClocked: 'prijavljen',
    presenceInferred: 'izvedeno',
    presenceReportTitle: 'Za ovaj dan kolone predat je izvještaj',
    presenceClockedTitle: 'Kolona je prijavila dolazak, ali nije predala izvještaj',
    presenceInferredTitle: 'Nema AU zapisa za ovaj dan kolone — izvedeno jer je kolona bila aktivna prije i poslije tog dana',
    flagMarked: 'Označeno za rješavanje',
    flagQuantityDetail:
      'Količina daleko veća od svega što izvor realno može sadržati. Najveći stvarni dan kolone '
      + 'u evidenciji je 663 m, pa se sve preko 2.000 m označava za provjeru.',
    flagNegativeHoursDetail:
      'Negativni radni sati: smjena ima vrijeme završetka, ali je ono prije početka (ili je pauza '
      + 'duža od smjene), pa trajanje ispada manje od nule. Otvorene smjene bez odjave '
      + 'se ne označavaju — ti sati ostaju prazni, ne oduzimaju se.',
    noMatches: 'Nema rezultata',
    unnamed: '(bez naziva)',
    showingFirst: 'Prikazano prvih {max} — nastavite kucati da suzite izbor.',
  },
  bs: {
    crewDaysLabel: 'Dani kolona',
    dataAsOf: 'Podaci do',
    refreshed: 'osvježeno {ago}',
    justNow: 'upravo sada',
    minAgo: 'prije {count} min',
    hoursAgo: 'prije {count} h',
    daysAgo: { one: 'prije {count} dan', few: 'prije {count} dana', other: 'prije {count} dana' },
    exportExcel: 'Izvezi Excel',
    exportExcelRows: { one: 'Izvezi Excel · {count} red', few: 'Izvezi Excel · {count} reda', other: 'Izvezi Excel · {count} redova' },
    preparingExcel: 'Pripremam Excel…',
    preparingExcelLabel: 'Pripremam Excel',
    exportTooBig: 'Za izvoz suzite filter na najviše {max} redova · trenutno {now}. Dovoljan je period, projekat ili firma.',
    searchPlaceholder: 'ulica, mjesto, kolona, firma…',
    project: 'Projekat',
    allProjects: 'Svi projekti',
    findProject: 'Upišite naziv projekta…',
    firma: 'Firma',
    allFirms: 'Sve firme',
    findFirm: 'Upišite naziv firme…',
    record: 'Zapis',
    allRecords: 'Svi zapisi',
    reportFiled: 'Izvještaj predan',
    clockedInOnly: 'Samo prijavljen dolazak',
    inferred: 'Izvedeno',
    cabling: 'Kabliranje',
    allWork: 'Svi radovi',
    anyCabling: 'Bilo koje kabliranje',
    sortLabel: 'Sortiranje (ili dodirnite zaglavlje kolone)',
    sort: 'Sortiranje',
    newestFirst: 'Najnovije prvo',
    oldestFirst: 'Najstarije prvo',
    mostMetres: 'Najviše metara',
    highestSub: 'Najveći SUB €',
    crewAz: 'Kolona A–Z',
    mjestoAz: 'Mjesto A–Z',
    withWorkOnly: 'Samo s radom',
    positionColumns: 'Kolone pozicija',
    headers: 'Zaglavlja',
    headersFull: 'Puna',
    headersShort: 'Kratka',
    headersFullTitle: 'Puni nazivi u više redova',
    headersShortTitle: 'Kratki nazivi u jednom redu',
    hideEmptyColumns: 'Sakrij prazne kolone',
    zoom: 'Uvećanje',
    zoomOut: 'Umanji',
    zoomIn: 'Uvećaj',
    zoomReset: 'Vrati na 100%',
    showFilters: 'Prikaži filtere',
    hideFilters: 'Sakrij filtere',
    sheetSubtitle: {
      one: '{count} dan kolone · podaci do {date}',
      few: '{count} dana kolona · podaci do {date}',
      other: '{count} dana kolona · podaci do {date}',
    },
    showCrewDays: { one: 'Prikaži {count} dan kolone', few: 'Prikaži {count} dana kolona', other: 'Prikaži {count} dana kolona' },
    couldNotLoadMeasurements: 'Nije moguće učitati mjerenja.',
    tapToEdit: 'Dodirnite ćeliju, pa je uredite u traci',
    desktopEditHint: 'Kucajte za uređivanje · Enter/Tab potvrđuje · Esc otkazuje · ⌘/Ctrl+⌫ vraća vrijednost iz AU',
    readOnlyNeedsPermission: 'Samo za čitanje — za uređivanje je potrebno CanEditAufmass',
    correctedCells: {
      one: '{count} ispravljena ćelija na ovoj stranici',
      few: '{count} ispravljene ćelije na ovoj stranici',
      other: '{count} ispravljenih ćelija na ovoj stranici',
    },
    you: 'vi',
    gridAria: 'Aufmaß tabela',
    formulaBarAria: 'Traka formule — vrijednost odabrane ćelije',
    editSelectedCell: 'Uredite odabranu ćeliju…',
    readOnlyDerived: 'Samo za čitanje — izvedeno iz AU',
    clickToSort: 'Kliknite za sortiranje',
    editable: 'Može se uređivati',
    couldNotSave: 'Nije moguće spasiti ćeliju.',
    correctedBy: 'Ispravka: {name}, {date}. U AU: {original}',
    empty: '(prazno)',
    rtkChanged: 'RTK mjerenje izmijenjeno: Total Metri se promijenio za {delta} m nakon ispravke na karti.',
    romNotSet: 'korisnik nije unio podatak',
    presenceReport: 'izvještaj',
    presenceClocked: 'prijavljen',
    presenceInferred: 'izvedeno',
    presenceReportTitle: 'Za ovaj dan kolone predan je izvještaj',
    presenceClockedTitle: 'Kolona je prijavila dolazak, ali nije predala izvještaj',
    presenceInferredTitle: 'Nema AU zapisa za ovaj dan kolone — izvedeno jer je kolona bila aktivna prije i poslije tog dana',
    flagMarked: 'Označeno za rješavanje',
    flagQuantityDetail:
      'Količina daleko veća od svega što izvor realno može sadržavati. Najveći stvarni dan kolone '
      + 'u evidenciji je 663 m, pa se sve preko 2.000 m označava za provjeru.',
    flagNegativeHoursDetail:
      'Negativni radni sati: smjena ima vrijeme završetka, ali je ono prije početka (ili je pauza '
      + 'duža od smjene), pa trajanje ispada manje od nule. Otvorene smjene bez odjave '
      + 'se ne označavaju — ti sati ostaju prazni, ne oduzimaju se.',
    noMatches: 'Nema rezultata',
    unnamed: '(bez naziva)',
    showingFirst: 'Prikazano prvih {max} — nastavite kucati da suzite izbor.',
  },
  sr: {
    crewDaysLabel: 'Dani kolona',
    dataAsOf: 'Podaci do',
    refreshed: 'osveženo {ago}',
    justNow: 'upravo sada',
    minAgo: 'pre {count} min',
    hoursAgo: 'pre {count} h',
    daysAgo: { one: 'pre {count} dan', few: 'pre {count} dana', other: 'pre {count} dana' },
    exportExcel: 'Izvezi Excel',
    exportExcelRows: { one: 'Izvezi Excel · {count} red', few: 'Izvezi Excel · {count} reda', other: 'Izvezi Excel · {count} redova' },
    preparingExcel: 'Pripremam Excel…',
    preparingExcelLabel: 'Pripremam Excel',
    exportTooBig: 'Za izvoz suzite filter na najviše {max} redova · trenutno {now}. Dovoljan je period, projekat ili firma.',
    searchPlaceholder: 'ulica, mesto, kolona, firma…',
    project: 'Projekat',
    allProjects: 'Svi projekti',
    findProject: 'Upišite naziv projekta…',
    firma: 'Firma',
    allFirms: 'Sve firme',
    findFirm: 'Upišite naziv firme…',
    record: 'Zapis',
    allRecords: 'Svi zapisi',
    reportFiled: 'Izveštaj predat',
    clockedInOnly: 'Samo prijavljen dolazak',
    inferred: 'Izvedeno',
    cabling: 'Kabliranje',
    allWork: 'Svi radovi',
    anyCabling: 'Bilo koje kabliranje',
    sortLabel: 'Sortiranje (ili dodirnite zaglavlje kolone)',
    sort: 'Sortiranje',
    newestFirst: 'Najnovije prvo',
    oldestFirst: 'Najstarije prvo',
    mostMetres: 'Najviše metara',
    highestSub: 'Najveći SUB €',
    crewAz: 'Kolona A–Z',
    mjestoAz: 'Mesto A–Z',
    withWorkOnly: 'Samo sa radom',
    positionColumns: 'Kolone pozicija',
    headers: 'Zaglavlja',
    headersFull: 'Puna',
    headersShort: 'Kratka',
    headersFullTitle: 'Puni nazivi u više redova',
    headersShortTitle: 'Kratki nazivi u jednom redu',
    hideEmptyColumns: 'Sakrij prazne kolone',
    zoom: 'Uvećanje',
    zoomOut: 'Umanji',
    zoomIn: 'Uvećaj',
    zoomReset: 'Vrati na 100%',
    showFilters: 'Prikaži filtere',
    hideFilters: 'Sakrij filtere',
    sheetSubtitle: {
      one: '{count} dan kolone · podaci do {date}',
      few: '{count} dana kolona · podaci do {date}',
      other: '{count} dana kolona · podaci do {date}',
    },
    showCrewDays: { one: 'Prikaži {count} dan kolone', few: 'Prikaži {count} dana kolona', other: 'Prikaži {count} dana kolona' },
    couldNotLoadMeasurements: 'Nije moguće učitati merenja.',
    tapToEdit: 'Dodirnite ćeliju, pa je izmenite u traci',
    desktopEditHint: 'Kucajte za izmenu · Enter/Tab potvrđuje · Esc otkazuje · ⌘/Ctrl+⌫ vraća vrednost iz AU',
    readOnlyNeedsPermission: 'Samo za čitanje — za izmene je potrebno CanEditAufmass',
    correctedCells: {
      one: '{count} ispravljena ćelija na ovoj strani',
      few: '{count} ispravljene ćelije na ovoj strani',
      other: '{count} ispravljenih ćelija na ovoj strani',
    },
    you: 'vi',
    gridAria: 'Aufmaß tabela',
    formulaBarAria: 'Traka formule — vrednost odabrane ćelije',
    editSelectedCell: 'Izmenite odabranu ćeliju…',
    readOnlyDerived: 'Samo za čitanje — izvedeno iz AU',
    clickToSort: 'Kliknite za sortiranje',
    editable: 'Može se menjati',
    couldNotSave: 'Nije moguće sačuvati ćeliju.',
    correctedBy: 'Ispravka: {name}, {date}. U AU: {original}',
    empty: '(prazno)',
    rtkChanged: 'RTK merenje izmenjeno: Total Metri se promenio za {delta} m nakon ispravke na mapi.',
    romNotSet: 'korisnik nije unio podatak',
    presenceReport: 'izveštaj',
    presenceClocked: 'prijavljen',
    presenceInferred: 'izvedeno',
    presenceReportTitle: 'Za ovaj dan kolone predat je izveštaj',
    presenceClockedTitle: 'Kolona je prijavila dolazak, ali nije predala izveštaj',
    presenceInferredTitle: 'Nema AU zapisa za ovaj dan kolone — izvedeno jer je kolona bila aktivna pre i posle tog dana',
    flagMarked: 'Označeno za rešavanje',
    flagQuantityDetail:
      'Količina daleko veća od svega što izvor realno može sadržati. Najveći stvarni dan kolone '
      + 'u evidenciji je 663 m, pa se sve preko 2.000 m označava za proveru.',
    flagNegativeHoursDetail:
      'Negativni radni sati: smena ima vreme završetka, ali je ono pre početka (ili je pauza '
      + 'duža od smene), pa trajanje ispada manje od nule. Otvorene smene bez odjave '
      + 'se ne označavaju — ti sati ostaju prazni, ne oduzimaju se.',
    noMatches: 'Nema rezultata',
    unnamed: '(bez naziva)',
    showingFirst: 'Prikazano prvih {max} — nastavite kucati da suzite izbor.',
  },
})

export type AufmassKey = keyof typeof aufmassMessages.en
